import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import "./Settings.scss";
import { AuthContext } from "../../../contexts/AuthContext";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { resolveUrl } from "../../../utils/resolveUrl";
import {
  DynamicFileInput,
  DynamicInput,
  DynamicSelect,
} from "../../../components/Inputs/Inputs";
import AlertModal from "../../../components/AlertModal/AlertModal";
import Button from "../../../components/Button/Button";

const EMPTY = {
  id: null,
  title: "Chatbot AI",
  primary_color: "#004080",
  secondary_color: "#0f2b46",
  tertiary_color: "#ffffff",
  bubble_color: "#004080",
  agent_message_color: "#c9c9c9",
  visitor_message_color: "#67E76B",
  book_now_color: "#AA998A",
  bubble_icon: "", // URL/preview
  header_icon: "", // URL/preview
};

export default function Settings() {
  const { axiosAuth, accessibleGroups = [] } = useContext(AuthContext);

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [settingId, setSettingId] = useState(null);
  const [settings, setSettings] = useState({ ...EMPTY });

  // ficheiros
  const [bubbleFile, setBubbleFile] = useState(null);
  const [headerFile, setHeaderFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() =>
    setErrorModal({ open: false, title: "", message: "" })
  );

  const groupOptions = useMemo(() => {
    const base = (accessibleGroups || []).map((g) => ({
      value: String(g.id),
      label: g.name,
    }));
    return base;
  }, [accessibleGroups]);

  // helper para transformar array de objetos em objeto
  function pickSetting(data) {
    if (!data) return null;
    if (Array.isArray(data)) return data[0] ?? null;
    return data;
  }

  // carrega / inicia settings do grupo
  useEffect(() => {
    setSettingId(null);
    //setSettings({ ...EMPTY });
    setBubbleFile(null);
    setHeaderFile(null);

    if (!selectedGroupId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/chat-settings/group/${selectedGroupId}`
        );
        const s = pickSetting(data);
        if (!s) {
          openError(
            "Configuração não encontrada",
            "Não foi possível obter as definições deste grupo."
          );
          return;
        }
        setSettingId(s.id);
        setSettings({
          id: s.id,
          title: s.title,
          primary_color: s.primary_color,
          secondary_color: s.secondary_color,
          tertiary_color: s.tertiary_color,
          bubble_color: s.bubble_color,
          agent_message_color: s.agent_message_color,
          visitor_message_color: s.visitor_message_color,
          book_now_color: s.book_now_color,
          bubble_icon: s.bubble_icon || "",
          header_icon: s.header_icon || "",
        });
      } catch (err) {
        if (err?.response?.status !== 404) {
          openError(
            "Erro ao carregar definições",
            err?.response?.data?.detail || err.message
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedGroupId, axiosAuth, openError]);

  useEffect(() => {
    if (accessibleGroups.length > 1 && !selectedGroupId) {
      setSelectedGroupId(String(accessibleGroups[0]?.id));
    }
  }, [accessibleGroups, selectedGroupId]);

  // inputs texto/cor -> DynamicInput -> onChange(name,value)
  const handleChange = useCallback((name, value) => {
    setSettings((s) => ({ ...s, [name]: value }));
  }, []);

  // ficheiros DynamicFileInput passa evento nativo
  const handleFileChange = useCallback((e) => {
    const { name, files } = e.target || {};
    const file = files?.[0];
    if (!name || !file) return;

    const reader = new FileReader();
    reader.onload = () => setSettings((s) => ({ ...s, [name]: reader.result }));
    reader.readAsDataURL(file);

    if (name === "bubble_icon") setBubbleFile(file);
    if (name === "header_icon") setHeaderFile(file);
  }, []);

  // garante que temos o id da setting (recarrega se preciso)
  const ensureSettingId = useCallback(async () => {
    if (settingId) return settingId;
    if (!selectedGroupId) return null;
    try {
      const { data } = await axiosAuth.get(
        `/chat-settings/group/${selectedGroupId}`
      );
      const s = pickSetting(data);
      if (!s) return null;
      setSettingId(s.id);
      // também sincroniza os valores caso o user tenha entrado e clicado "guardar"
      setSettings({
        id: s.id,
        title: s.title,
        primary_color: s.primary_color,
        secondary_color: s.secondary_color,
        tertiary_color: s.tertiary_color,
        bubble_color: s.bubble_color,
        agent_message_color: s.agent_message_color,
        visitor_message_color: s.visitor_message_color,
        book_now_color: s.book_now_color,
        bubble_icon: s.bubble_icon || "",
        header_icon: s.header_icon || "",
      });
      return s.id;
    } catch {
      return null;
    }
  }, [axiosAuth, selectedGroupId, settingId]);

  // guardar PUT multipart
  const handleSave = useCallback(async () => {
    if (!selectedGroupId) {
      openError("Validação", "A sua conta não tem grupo associado.");
      return;
    }
    setSaving(true);
    try {
      const id = await ensureSettingId(); // retorna undefined
      if (!id) {
        openError(
          "Configuração não encontrada",
          "Não foi possível localizar as definições deste grupo."
        );
        return;
      }

      const payload = {
        group_id: Number(selectedGroupId), // opcional, mas inofensivo
        title: settings.title,
        primary_color: settings.primary_color,
        secondary_color: settings.secondary_color,
        tertiary_color: settings.tertiary_color,
        bubble_color: settings.bubble_color,
        agent_message_color: settings.agent_message_color,
        visitor_message_color: settings.visitor_message_color,
        book_now_color: settings.book_now_color,
      };

      const fd = new FormData();
      fd.append("payload", JSON.stringify(payload));
      if (bubbleFile) fd.append("bubble_icon", bubbleFile);
      if (headerFile) fd.append("header_icon", headerFile);

      const resp = await axiosAuth.put(`/chat-settings/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const d = resp.data;
      setSettingId(d.id);
      setSettings((s) => ({
        ...s,
        title: d.title,
        primary_color: d.primary_color,
        secondary_color: d.secondary_color,
        tertiary_color: d.tertiary_color,
        bubble_color: d.bubble_color,
        agent_message_color: d.agent_message_color,
        visitor_message_color: d.visitor_message_color,
        book_now_color: d.book_now_color,
        bubble_icon: d.bubble_icon || s.bubble_icon,
        header_icon: d.header_icon || s.header_icon,
      }));
      setBubbleFile(null);
      setHeaderFile(null);
    } catch (err) {
      openError(
        "Erro ao guardar definições",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
    }
  }, [
    selectedGroupId,
    ensureSettingId,
    settings,
    bubbleFile,
    headerFile,
    axiosAuth,
    openError,
  ]);

  return (
    <div className="my-settings">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      <PageTitle title="Definições" />
      <div className="filters">
        <DynamicSelect
          label="Clients"
          placeholder="Escolher um grupo"
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          disabled={loading}
          options={groupOptions}
        />
      </div>
      <div className="container">
        <div className="settings-form">
          <div className="row two-col">
            <DynamicInput
              label="Título"
              type="text"
              name="title"
              value={settings.title}
              onChange={handleChange}
              required
            />
            <DynamicInput
              label="Cor book now"
              type="color"
              name="book_now_color"
              value={settings.book_now_color}
              onChange={handleChange}
              required
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              label="Cor primária"
              type="color"
              name="primary_color"
              value={settings.primary_color}
              onChange={handleChange}
              required
            />
            <DynamicInput
              label="Cor secundária"
              type="color"
              name="secondary_color"
              value={settings.secondary_color}
              onChange={handleChange}
              required
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              label="Cor terciária"
              type="color"
              name="tertiary_color"
              value={settings.tertiary_color}
              onChange={handleChange}
              required
            />
            <DynamicInput
              label="Cor da bolha"
              type="color"
              name="bubble_color"
              value={settings.bubble_color}
              onChange={handleChange}
              required
            />
          </div>
          <div className="row two-col">
            <DynamicInput
              label="Cor da msg. do agente"
              type="color"
              name="agent_message_color"
              value={settings.agent_message_color}
              onChange={handleChange}
              required
            />
            <DynamicInput
              label="Cor da msg. do visitante"
              type="color"
              name="visitor_message_color"
              value={settings.visitor_message_color}
              onChange={handleChange}
              required
            />
          </div>
          <div className="row two-col">
            <DynamicFileInput
              label="Ícone da bolha"
              name="bubble_icon"
              accept="image/*"
              onChange={handleFileChange}
            />
            <DynamicFileInput
              label="Ícone do header"
              name="header_icon"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
          <div className="save-all">
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
            >
              Guardar
            </Button>
          </div>
          <div className="previews">
            <h3>Pré-visualização dos ícones</h3>
            <div className="icons">
              {settings.bubble_icon && (
                <div className="icon-preview">
                  <label>Ícone do balão</label>
                  <div
                    className="image"
                    style={{ background: settings.bubble_color }}
                  >
                    <img
                      src={resolveUrl(settings.bubble_icon)}
                      alt="Bubble Icon Preview"
                    />
                  </div>
                </div>
              )}
              {settings.header_icon && (
                <div className="icon-preview">
                  <label>Ícone do header</label>
                  <div className="image">
                    <img
                      src={resolveUrl(settings.header_icon)}
                      alt="Header Icon Preview"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
